/**
 * App Component
 * 
 * Main application component with routing configuration.
 * Wrapped with ErrorBoundary for graceful error handling.
 */

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary, FullPageLoading } from './components/common';
import { NotificationProvider } from './shared/components/ui';
import { ROUTES } from './constants';

// Pages
import Login from './pages/Login';
import Activate from './pages/Activate';
import Dashboard from './pages/Dashboard';
import Unauthorized from './pages/Unauthorized';
// more

// Components
import ProtectedRoute from './components/security/ProtectedRoute';

// Lazy load sub-applications
const HubManagementRoutes = lazy(() => import('./apps/hub-management/routes'));
const HaatRequestsRoutes = lazy(() => import('./apps/haat-requests/routes'));
const TaskifyRoutes = lazy(() => import('./apps/taskify-management/routes'));
const CampaignsRoutes = lazy(() => import('./apps/campaigns-management/routes'));
const DevicesRoutes = lazy(() => import('./apps/devices-management/routes'));
const CareersRoutes = lazy(() => import('./apps/careers-management/routes'));
const CourierConnectRoutes = lazy(() => import('./apps/courier-connect-management/routes'));
const PartnerConnectRoutes = lazy(() => import('./apps/partner-connect-management/routes'));
const PartnerMsgRoutes = lazy(() => import('./apps/partner-msg-management/routes'));
const ProductTeamRoutes = lazy(() => import('./apps/product-team-management/routes'));
const QlikAccessRoutes = lazy(() => import('./apps/qlik-access-management/routes'));
const CustomerServicePollRoutes = lazy(() => import('./apps/customer-service-poll/routes'));
const CourierDocVerificationRoutes = lazy(() => import('./apps/courier-document-verification/routes'));
const CallCenterRoutingRoutes = lazy(() => import('./apps/call-center-routing/routes'));
const GlobalManagementRoutes = lazy(() => import('./apps/global-management/routes'));
const HaatScreensRoutes = lazy(() => import('./apps/haat-screens/routes'));
const MarketResearchRoutes = lazy(() => import('./apps/market-research-management/routes'));
// Disabled for now while prod memory is investigated.
// const OnboardingSchedulerRoutes = lazy(() => import('./apps/onboarding-scheduler/routes'));
const TargetsRoutes = lazy(() => import('./apps/targets/routes'));
const SupplierPaymentsRoutes = lazy(() => import('./apps/supplier-payments/routes'));

// Public mini-app forms (login-free, token-gated)
const PublicFormPage = lazy(() => import('./public-forms/PublicFormPage'));

/**
 * App Routes Component
 * Separated for cleaner structure
 */
const AppRoutes: React.FC = () => {
  const { isLoading } = useAuth();
  const { t } = useTranslation('common');
  const location = useLocation();

  // Public mini-app forms render without waiting for (or requiring) hub auth.
  const isPublicForm = location.pathname.startsWith(`${ROUTES.PUBLIC_FORMS_BASE}/`);

  // Show loading while checking authentication (skipped for public forms)
  if (isLoading && !isPublicForm) {
    return <FullPageLoading message={t('loading.authCheck')} />;
  }

  return (
    <Suspense fallback={<FullPageLoading message={t('loading.appLoading')} />}>
      <Routes>
        {/* Public routes */}
        <Route path={ROUTES.LOGIN} element={<Login />} />
        <Route path={ROUTES.ACTIVATE} element={<Activate />} />
        <Route path={ROUTES.UNAUTHORIZED} element={<Unauthorized />} />
        <Route path={`${ROUTES.PUBLIC_FORMS_BASE}/:formSlug/:token`} element={<PublicFormPage />} />
        <Route path={`${ROUTES.PUBLIC_FORMS_BASE}/:formSlug`} element={<PublicFormPage />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />

          {/* Sub-applications */}
          <Route path="/hub-management/*" element={<HubManagementRoutes />} />
          <Route path="/haat-requests/*" element={<HaatRequestsRoutes />} />
          <Route path="/taskify-management/*" element={<TaskifyRoutes />} />
          <Route path="/campaigns-management/*" element={<CampaignsRoutes />} />
          <Route path="/devices-management/*" element={<DevicesRoutes />} />
          <Route path="/careers-management/*" element={<CareersRoutes />} />
          <Route path="/courier-connect-management/*" element={<CourierConnectRoutes />} />
          <Route path="/partner-connect-management/*" element={<PartnerConnectRoutes />} />
          <Route path="/partner-msg-management/*" element={<PartnerMsgRoutes />} />
          <Route path="/product-team/*" element={<ProductTeamRoutes />} />
          <Route path="/qlik-access-management/*" element={<QlikAccessRoutes />} />
          <Route path="/customer-service-poll/*" element={<CustomerServicePollRoutes />} />
          <Route path="/courier-document-verification/*" element={<CourierDocVerificationRoutes />} />
          <Route path="/call-center-routing/*" element={<CallCenterRoutingRoutes />} />
          <Route path="/global-management/*" element={<GlobalManagementRoutes />} />
          <Route path="/haat-screens/*" element={<HaatScreensRoutes />} />
          <Route path="/market-research-management/*" element={<MarketResearchRoutes />} />
          {/* <Route path="/onboarding-scheduler/*" element={<OnboardingSchedulerRoutes />} /> */}
          <Route path="/targets/*" element={<TargetsRoutes />} />
          <Route path="/supplier-payments/*" element={<SupplierPaymentsRoutes />} />
        </Route>

        {/* Redirects */}
        <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.DASHBOARD} replace />} />
        <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      </Routes>
    </Suspense>
  );
};

/**
 * Main App Component
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AppRoutes />
      <NotificationProvider />
    </ErrorBoundary>
  );
};

export default App;
